import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Baby, Pencil, Trash2, Save, X } from "lucide-react";

/**
 * AllKidProfilesAdminTab — admin view of every kid profile across every
 * family on the platform. Admins (auto-treated as parents) can edit
 * avatars, rename, adjust daily limits, and delete any profile.
 */
export default function AllKidProfilesAdminTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // whole row being edited

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/kids/all-profiles?limit=500");
      setRows(r.data?.profiles || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      const body = {
        name: editing.name,
        avatar_emoji: editing.avatar_emoji,
        avatar_image_url: editing.avatar_image_url || "",
        daily_limit_min: Number(editing.daily_limit_min || 60),
      };
      await api.patch(`/admin/kids/profiles/${editing.profile_id}`, body);
      toast.success("Profile updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.name}'s profile? This wipes their watch history too.`)) return;
    try {
      await api.delete(`/admin/kids/profiles/${row.profile_id}`);
      toast.success("Profile deleted");
      setRows((r) => r.filter((x) => x.profile_id !== row.profile_id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="all-kid-profiles-tab" className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl text-white flex items-center gap-2">
          <Baby className="w-6 h-6 text-sky-400" /> All Kid Profiles
        </h2>
        <p className="text-white/60 text-sm mt-1">
          Every kid profile platform-wide. {rows.length} total.
        </p>
      </div>

      {loading && <div className="text-white/50 text-sm">Loading profiles…</div>}

      {!loading && rows.length === 0 && (
        <div data-testid="all-profiles-empty" className="text-white/50 text-sm bg-black/30 border border-dashed border-white/10 rounded-xl p-6">No kid profiles on the platform yet.</div>
      )}

      {!loading && rows.length > 0 && (
        <div data-testid="all-profiles-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((p) => (
            <div key={p.profile_id} data-testid={`all-profile-${p.profile_id}`} className="bg-black/40 border border-white/10 rounded-xl p-4">
              <div className="flex items-start gap-3">
                {p.avatar_image_url
                  ? <img src={p.avatar_image_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  : <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl">{p.avatar_emoji || "⭐"}</div>
                }
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate">{p.name}</div>
                  <div className="text-white/50 text-xs truncate">{p.parent_email || "no parent email"}</div>
                  <div className="text-white/40 text-[10px] truncate">{p.handle || p.profile_id}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-white/60">
                <span>{p.daily_limit_min || 60} min/day</span>
                {p.window_start_hh != null && p.window_end_hh != null && (
                  <span>· {p.window_start_hh}:00 – {p.window_end_hh}:00</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  data-testid={`all-profile-edit-${p.profile_id}`}
                  onClick={() => setEditing({ ...p })}
                  className="text-xs font-bold bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                ><Pencil className="w-3 h-3" /> Edit</button>
                <button
                  data-testid={`all-profile-delete-${p.profile_id}`}
                  onClick={() => del(p)}
                  className="text-xs font-bold bg-red-500/80 hover:bg-red-500 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                ><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div data-testid="all-profile-edit-modal" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#11121a] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-xl text-white">Edit {editing.name}</h3>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <label className="block text-xs text-white/60 mb-1">Name</label>
            <input
              data-testid="all-profile-edit-name"
              value={editing.name || ""}
              onChange={(e) => setEditing({ ...editing, name: e.target.value.slice(0, 40) })}
              className="w-full bg-white/5 border border-white/20 rounded-md px-3 py-2 text-white text-sm mb-3"
            />
            <label className="block text-xs text-white/60 mb-1">Emoji avatar</label>
            <input
              data-testid="all-profile-edit-emoji"
              value={editing.avatar_emoji || ""}
              onChange={(e) => setEditing({ ...editing, avatar_emoji: e.target.value.slice(0, 4) })}
              className="w-20 bg-white/5 border border-white/20 rounded-md px-3 py-2 text-white text-2xl text-center mb-3"
            />
            <label className="block text-xs text-white/60 mb-1">Avatar image URL (optional)</label>
            <input
              data-testid="all-profile-edit-img"
              value={editing.avatar_image_url || ""}
              onChange={(e) => setEditing({ ...editing, avatar_image_url: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-md px-3 py-2 text-white text-xs mb-3"
              placeholder="https://…"
            />
            <label className="block text-xs text-white/60 mb-1">Daily limit (min)</label>
            <input
              type="number"
              min={10}
              max={600}
              data-testid="all-profile-edit-limit"
              value={editing.daily_limit_min || 60}
              onChange={(e) => setEditing({ ...editing, daily_limit_min: Number(e.target.value) })}
              className="w-32 bg-white/5 border border-white/20 rounded-md px-3 py-2 text-white text-sm mb-4"
            />
            <button data-testid="all-profile-save-btn" onClick={save} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-4 py-2 rounded-full text-sm inline-flex items-center gap-1"><Save className="w-4 h-4" /> Save changes</button>
          </div>
        </div>
      )}
    </section>
  );
}
